import { useId } from "react";
import { useTranslation } from "react-i18next";

/**
 * Route pending loader shown by the router's `defaultPendingComponent`
 * during navigations (AD-1729).
 *
 * Rendered as an inline SVG (not an <img>) so the logo inherits the
 * document's class-based theme via `currentColor` — `text-foreground`
 * resolves to the themed token in both light and dark. An <img> is an
 * isolated document and could only follow the OS `prefers-color-scheme`,
 * which mismatched the in-app theme (the bug this fixes). The sweep
 * animation and reduced-motion handling live in `styles/globals.css`
 * (`.adge-route-loader__*`).
 */
export function RoutePendingLoader() {
  const { t } = useTranslation();
  // Unique per-instance ids so multiple loaders on one page can't collide on
  // `url(#…)` mask/gradient references (matches the codebase useId() convention).
  const maskId = useId();
  const gradientId = useId();
  return (
    <div className="fixed inset-0 grid place-items-center text-foreground">
      <svg
        viewBox="0 0 444.27 235.03"
        className="h-auto w-[90px]"
        role="status"
        aria-label={t("common.loading")}
      >
        <defs>
          <mask id={maskId}>
            <path
              fill="white"
              d="M420.4,10.72c-98.2,0-179.41,73.2-196.45,166.48h-3.63C203.29,83.92,122.08,10.72,23.88,10.72h-13.54v64.65h13.54c115.37,0,154.8,58.84,165.93,148.94h64.65c11.12-90.1,50.56-148.94,165.93-148.94h13.54V10.72h-13.54Z"
            />
          </mask>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
            <stop offset="42%" stopColor="currentColor" stopOpacity="0.7" />
            <stop offset="50%" stopColor="currentColor" stopOpacity="1" />
            <stop offset="58%" stopColor="currentColor" stopOpacity="0.7" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <g mask={`url(#${maskId})`}>
          <rect
            className="adge-route-loader__base"
            x="0"
            y="0"
            width="444.27"
            height="235.03"
          />
          <rect
            className="adge-route-loader__fill"
            x="0"
            y="0"
            width="444.27"
            height="235.03"
            fill={`url(#${gradientId})`}
          />
        </g>
      </svg>
    </div>
  );
}
